import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';

type UpdateRouterOptions = {
  shouldReplaceHistory: boolean;
  scrollToTopOfPage: boolean;
};

type RemoveAllQueryParams = {
  listingAlias: string;
  shouldReplaceHistory?: boolean;
  scrollToTopOfPage?: boolean;
};

type UpdateQueryParams = {
  queryParamValues: Record<string, string | Array<string> | null>;
  // Override `router.pathname` — useful on catch-all routes to keep a key in the query string
  // (e.g. pass `/lead-management` to write `?listingAlias=foo` instead of `/lead-management/foo`).
  pathname?: string;
  shouldReplaceHistory?: boolean;
  scrollToTopOfPage?: boolean;
};

export default () => {
  const router = useRouter();

  const updateRouter = (
    updatedRouter: Pick<NextRouter, 'pathname' | 'query'>,
    { shouldReplaceHistory, scrollToTopOfPage }: UpdateRouterOptions,
  ) => {
    return shouldReplaceHistory
      ? router.replace(updatedRouter, undefined, { scroll: scrollToTopOfPage, shallow: true })
      : router.push(updatedRouter, undefined, { scroll: scrollToTopOfPage, shallow: true });
  };

  const removeAllQueryParams = ({
    listingAlias,
    shouldReplaceHistory = false,
    scrollToTopOfPage = false,
  }: RemoveAllQueryParams) => {
    return updateRouter(
      { pathname: '/lead-management/[[...listingAlias]]', query: { listingAlias } },
      { shouldReplaceHistory, scrollToTopOfPage },
    );
  };

  const updateQueryParams = ({
    queryParamValues,
    pathname,
    shouldReplaceHistory = false,
    scrollToTopOfPage = false,
  }: UpdateQueryParams) => {
    const existingQueryParams = { ...router.query };
    Object.entries(queryParamValues).map(async ([key, value]) => {
      if (value === null) {
        delete existingQueryParams[key];
      } else {
        existingQueryParams[key] = value;
      }
    });

    // Cast bypasses typed-routes — Next.js still routes the bare path to its catch-all page at runtime.
    const updatedRouter = {
      pathname: (pathname ?? router.pathname) as typeof router.pathname,
      query: existingQueryParams,
    };

    return updateRouter(updatedRouter, { shouldReplaceHistory, scrollToTopOfPage });
  };

  return {
    removeAllQueryParams,
    updateQueryParams,
  };
};
